import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { Node, Edge, addEdge, Connection } from 'reactflow';
import { getNodeColor, createDefaultNode } from '@/features/editor/nodes';
import { getReactFlowNodeType } from './utils';
import { nextNodePosition } from './nextNodePosition';
import { detachReferencesTo } from '@/features/editor/utils/detachReferencesTo';

interface UseFlowOperationsProps {
  nodes: Node[];
  setNodes: (nodes: Node[] | ((nodes: Node[]) => Node[])) => void;
  setEdges: (edges: Edge[] | ((edges: Edge[]) => Edge[])) => void;
  setSelectedNode: Dispatch<SetStateAction<Node | null>>;
}

export function useFlowOperations({
  nodes,
  setNodes,
  setEdges,
  setSelectedNode,
}: UseFlowOperationsProps) {
  const getPageBranchElements = (elements: any[] = []) =>
    elements.filter((element) =>
      element.createBranches === true ||
      (element.type === 'Button' && element.action === 'navigate')
    );

  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges((eds) => {
        // Find the source node to check its type
        const sourceNode = nodes.find((n) => n.id === connection.source);

        // For nodes that only support single 'next' (not If, Switch, DecisionPoint),
        // remove any existing edges from the same source
        const nodesThatBranch = ['if', 'decisionPoint'];
        const nodesThatHaveCases = ['switch'];

        if (sourceNode && !nodesThatBranch.includes(sourceNode.type || '') && !nodesThatHaveCases.includes(sourceNode.type || '')) {
          // Remove existing edges from this source
          const filteredEdges = eds.filter((edge) => edge.source !== connection.source);
          return addEdge(connection, filteredEdges);
        }

        return addEdge(connection, eds);
      });
    },
    [setEdges, nodes]
  );

  const addNode = useCallback((type: string) => {
    const id = `${type.toLowerCase()}-${Date.now()}`;
    const rfType = getReactFlowNodeType(type);
    const data = { ...createDefaultNode(type, id), color: getNodeColor(type) };
    // Use the functional setState form so nextNodePosition always receives the
    // current nodes array.  A plain closure would capture a stale snapshot if
    // addNode were called multiple times before React re-renders (e.g. in
    // automated tests), causing all new nodes to land at the same position.
    setNodes((nds) => {
      const newNode: Node = {
        id,
        type: rfType,
        position: nextNodePosition(nds),
        data,
      };
      return [...nds, newNode];
    });
    return id;
  }, [setNodes]);

  const deleteNode = useCallback((nodeId: string) => {
    // detachReferencesTo removes the node AND clears every branch reference
    // (MultiChoice, YesNo, Button) pointing at it.  Using the same helper as
    // onNodesChangeWrapped ensures both deletion paths produce identical results.
    setNodes((nds) => detachReferencesTo(nds, [nodeId]));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
  }, [setNodes, setEdges, setSelectedNode]);

  const createPageFromBranchStub = useCallback((stubNode: Node) => {
    const { parentId, branchType, branchIndex, elementId, branchLabel } = stubNode.data;
    if (!parentId) return;

    const parentNode = nodes.find((n) => n.id === parentId);
    if (!parentNode || parentNode.data.type !== 'Page') return;

    const newPageId = `page-${Date.now()}`;
    const pageTitle = branchLabel ? `${branchLabel} Page` : 'New Page';
    const newPageNode: Node = {
      id: newPageId,
      type: 'page',
      position: { ...stubNode.position },
      data: {
        ...createDefaultNode('Page', newPageId),
        title: pageTitle,
        label: pageTitle,
        color: getNodeColor('Page'),
      },
    };

    let branchEdge: Edge | null = null;
    const updatedElements = (parentNode.data.elements || []).map((element: any) => {
      if (element.type === 'YesNo' && (branchType === 'yes' || branchType === 'no')) {
        branchEdge = {
          id: `${parentId}-${branchType}-${newPageId}`,
          source: parentId,
          target: newPageId,
          label: branchLabel || (branchType === 'yes' ? element.yesLabel || 'Yes' : element.noLabel || 'No'),
          style: { stroke: branchType === 'yes' ? '#10b981' : '#ef4444', strokeWidth: 2 },
        };

        return {
          ...element,
          branches: {
            ...(element.branches || {}),
            [branchType]: newPageId,
          },
        };
      }

      if (element.type === 'MultiChoice' && branchType === 'choice') {
        const updatedBranches = [...(element.branches || [])];
        const targetIndex = typeof branchIndex === 'number' ? branchIndex : -1;
        const option = element.options?.[targetIndex] || branchLabel || `Option ${targetIndex + 1}`;

        if (targetIndex >= 0) {
          updatedBranches[targetIndex] = {
            ...(updatedBranches[targetIndex] || { option }),
            targetPageId: newPageId,
          };
        }

        branchEdge = {
          id: `${parentId}-choice${targetIndex}-${newPageId}`,
          source: parentId,
          target: newPageId,
          label: branchLabel || option,
          style: { stroke: '#6366f1', strokeWidth: 2 },
        };

        return {
          ...element,
          branches: updatedBranches,
        };
      }

      if (element.type === 'Button' && branchType === 'button' && element.id === elementId) {
        branchEdge = {
          id: `${parentId}-button-${elementId}-${newPageId}`,
          source: parentId,
          target: newPageId,
          label: branchLabel || element.buttonText || element.label || 'Button',
          style: { stroke: '#415A91', strokeWidth: 2 },
        };

        return {
          ...element,
          targetPageId: newPageId,
        };
      }

      return element;
    });

    setNodes((currentNodes) => [
      ...currentNodes
        .filter((node) => node.id !== stubNode.id)
        .map((node) =>
          node.id === parentId
            ? { ...node, data: { ...node.data, elements: updatedElements } }
            : node
        ),
      newPageNode,
    ]);

    setEdges((currentEdges) => {
      const withoutStubEdge = currentEdges.filter((edge) => edge.target !== stubNode.id);
      return branchEdge ? [...withoutStubEdge, branchEdge] : withoutStubEdge;
    });

    setSelectedNode(newPageNode);
  }, [nodes, setNodes, setEdges, setSelectedNode]);

  const handleNodeClick = useCallback((_: any, node: Node) => {
    // Branch stubs are placeholders; clicking one creates and wires a target page.
    if (node.type === 'branchStub' && node.data.parentId) {
      createPageFromBranchStub(node);
      return;
    }
    setSelectedNode(node);
  }, [createPageFromBranchStub, setSelectedNode]);

  const updateNodeData = useCallback((nodeId: string, data: any) => {
    const mergeNodeData = (node: Node) => ({ ...node, data: { ...node.data, ...data } });

    // If this is a Page node and elements changed, recalculate edges and stubs
    if (data.type === 'Page' && data.elements) {
      setNodes((currentNodes) => {
        // First, update the node data
        const updatedNodes = currentNodes.map((n) =>
          n.id === nodeId ? mergeNodeData(n) : n
        );

        // Remove old stub nodes for this page
        const withoutStubs = updatedNodes.filter(
          (n) => !(n.type === 'branchStub' && n.data.parentId === nodeId)
        );

        // Find the updated node
        const updatedNode = withoutStubs.find((n) => n.id === nodeId);
        if (!updatedNode) return updatedNodes;

        // Check for branching/navigation elements
        const branchElements = getPageBranchElements(data.elements || []);
        const newStubNodes: Node[] = [];
        const newBranchEdges: any[] = [];

        branchElements.forEach((branchingElement: any) => {
          // Handle YesNo element branches
          if (branchingElement.type === 'YesNo' && branchingElement.branches) {
            const yesTarget = branchingElement.branches.yes;
            const noTarget = branchingElement.branches.no;
            const yesLabel = branchingElement.yesLabel || 'Yes';
            const noLabel = branchingElement.noLabel || 'No';

            if (yesTarget) {
              newBranchEdges.push({
                id: `${nodeId}-yes-${yesTarget}`,
                source: nodeId,
                target: yesTarget,
                label: yesLabel,
                style: { stroke: '#10b981', strokeWidth: 2 },
              });
            } else {
              const stubId = `${nodeId}-stub-yes`;
              newStubNodes.push({
                id: stubId,
                type: 'branchStub',
                position: {
                  x: (updatedNode.position?.x || 0) - 120,
                  y: (updatedNode.position?.y || 0) + 150,
                },
                data: { branchLabel: yesLabel, parentId: nodeId, branchType: 'yes' },
              });
              newBranchEdges.push({
                id: `${nodeId}-yes-${stubId}`,
                source: nodeId,
                target: stubId,
                label: yesLabel,
                style: { stroke: '#10b981', strokeWidth: 2, strokeDasharray: '5,5' },
              });
            }

            if (noTarget) {
              newBranchEdges.push({
                id: `${nodeId}-no-${noTarget}`,
                source: nodeId,
                target: noTarget,
                label: noLabel,
                style: { stroke: '#ef4444', strokeWidth: 2 },
              });
            } else {
              const stubId = `${nodeId}-stub-no`;
              newStubNodes.push({
                id: stubId,
                type: 'branchStub',
                position: {
                  x: (updatedNode.position?.x || 0) + 120,
                  y: (updatedNode.position?.y || 0) + 150,
                },
                data: { branchLabel: noLabel, parentId: nodeId, branchType: 'no' },
              });
              newBranchEdges.push({
                id: `${nodeId}-no-${stubId}`,
                source: nodeId,
                target: stubId,
                label: noLabel,
                style: { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '5,5' },
              });
            }
          }

          // Handle MultiChoice element branches
          if (branchingElement.type === 'MultiChoice' && branchingElement.branches) {
            // Ignore branches whose option no longer exists. Editing now keeps
            // the two in step (see utils/multiChoiceOptions), but flows saved
            // before that still carry orphans, and drawing them is what put a
            // phantom edge and an empty extra slot on the canvas.
            const liveOptions: string[] = branchingElement.options || [];
            const activeBranches = branchingElement.branches.filter((branch: any) =>
              liveOptions.includes(branch.option),
            );

            activeBranches.forEach((branch: any, idx: number) => {
              if (branch.targetPageId) {
                newBranchEdges.push({
                  id: `${nodeId}-choice${idx}-${branch.targetPageId}`,
                  source: nodeId,
                  target: branch.targetPageId,
                  label: branch.option,
                  style: { stroke: '#6366f1', strokeWidth: 2 },
                });
              } else {
                const stubId = `${nodeId}-stub-choice${idx}`;
                // Spread across the branches actually drawn, not the raw list —
                // otherwise orphans leave a gap in the fan of stubs.
                const totalBranches = activeBranches.length;
                const spacing = 180;
                const startOffset = -((totalBranches - 1) * spacing) / 2;

                newStubNodes.push({
                  id: stubId,
                  type: 'branchStub',
                  position: {
                    x: (updatedNode.position?.x || 0) + startOffset + (idx * spacing),
                    y: (updatedNode.position?.y || 0) + 150,
                  },
                  data: { branchLabel: branch.option, parentId: nodeId, branchType: 'choice', branchIndex: idx },
                });
                newBranchEdges.push({
                  id: `${nodeId}-choice${idx}-${stubId}`,
                  source: nodeId,
                  target: stubId,
                  label: branch.option,
                  style: { stroke: '#6366f1', strokeWidth: 2, strokeDasharray: '5,5' },
                });
              }
            });
          }

          if (branchingElement.type === 'Button') {
            const targetPageId = branchingElement.targetPageId;
            const label = branchingElement.buttonText || branchingElement.label || 'Button';

            if (targetPageId) {
              newBranchEdges.push({
                id: `${nodeId}-button-${branchingElement.id}-${targetPageId}`,
                source: nodeId,
                target: targetPageId,
                label,
                style: { stroke: '#415A91', strokeWidth: 2 },
              });
            } else {
              const stubId = `${nodeId}-stub-button-${branchingElement.id}`;
              newStubNodes.push({
                id: stubId,
                type: 'branchStub',
                position: {
                  x: updatedNode.position?.x || 0,
                  y: (updatedNode.position?.y || 0) + 150,
                },
                data: { branchLabel: label, parentId: nodeId, branchType: 'button', elementId: branchingElement.id },
              });
              newBranchEdges.push({
                id: `${nodeId}-button-${branchingElement.id}-${stubId}`,
                source: nodeId,
                target: stubId,
                label,
                style: { stroke: '#415A91', strokeWidth: 2, strokeDasharray: '5,5' },
              });
            }
          }
        });

        // Remove old branch-related edges from this node
        setEdges((currentEdges) => {
          const withoutOldBranchEdges = currentEdges.filter(
            (e) => !(e.source === nodeId && (e.id.includes('-yes-') || e.id.includes('-no-') || e.id.includes('-choice') || e.id.includes('-button-')))
          );
          return [...withoutOldBranchEdges, ...newBranchEdges];
        });

        return [...withoutStubs, ...newStubNodes];
      });
    } else {
      // For non-Page nodes, just update the data
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? mergeNodeData(n) : n))
      );
    }

    setSelectedNode((current) =>
      current?.id === nodeId ? { ...current, data: { ...current.data, ...data } } : current
    );
  }, [setNodes, setEdges, setSelectedNode]);

  // Watch for nodes marked for stub regeneration.
  //
  // The marker must be cleared by setting it false, not by omitting it:
  // updateNodeData MERGES its argument into the existing data, so a key left
  // out of the object survives the update. Omitting it meant the marker was
  // still set on the next render, the effect fired again, and the editor died
  // with "Maximum update depth exceeded".
  useEffect(() => {
    nodes.forEach((node) => {
      if (node.type === 'page' && node.data._regenerateStubs) {
        updateNodeData(node.id, { ...node.data, _regenerateStubs: false });
      }
    });
  }, [nodes, updateNodeData]);

  return {
    onConnect,
    addNode,
    deleteNode,
    updateNodeData,
    handleNodeClick,
  };
}
